import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

// Resolve the Node API base URL (same pattern as the rest of the app).
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
  return `${protocol}//${hostname}`;
};

const FREE_FEATURES = [
  'Top Pick (our highest-conviction idea)',
  '1 Breakout Pick preview',
  '1 Value Pick preview',
  'Live performance tracking',
];

const PREMIUM_FEATURES = [
  'Everything in Free, plus:',
  'All Breakout Picks (full list)',
  'All Value Picks (full list)',
  'AI Risk Review on every pick',
  'Fundamentals: P/E, EPS, growth, analyst targets',
  'Full multi-indicator analysis charts',
  'Monthly sector-rotation strategy',
];

const Check = () => (
  <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
  </svg>
);

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const isPremium = (user?.subscription || 'free').toLowerCase() === 'premium';

  const startCheckout = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiUrl()}/api/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.message || 'Could not start checkout.');
      }
      window.location.href = data.url; // redirect to Stripe hosted checkout
    } catch (e) {
      toast.error(e.message || 'Checkout failed.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Upgrade to Premium</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Unlock every pick, AI risk reviews, and full analysis. One simple annual plan.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 max-w-3xl mx-auto">
          {/* Free */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col">
            <h2 className="text-xl font-bold text-gray-900">Free</h2>
            <div className="mt-4 mb-6">
              <span className="text-4xl font-extrabold text-gray-900">$0</span>
              <span className="text-gray-500">/forever</span>
            </div>
            <ul className="space-y-3 flex-1">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check />{f}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <span className="block w-full text-center px-5 py-3 rounded-lg border border-gray-300 text-gray-500 font-semibold">
                {!isPremium ? 'Current plan' : 'Free plan'}
              </span>
            </div>
          </div>

          {/* Premium */}
          <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-500 p-8 flex flex-col relative">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
              BEST VALUE
            </span>
            <h2 className="text-xl font-bold text-gray-900">Premium</h2>
            <div className="mt-4 mb-1">
              <span className="text-4xl font-extrabold text-gray-900">$99</span>
              <span className="text-gray-500">/year</span>
            </div>
            <p className="text-xs text-gray-400 mb-6">Billed annually · about $8.25/month</p>
            <ul className="space-y-3 flex-1">
              {PREMIUM_FEATURES.map((f, i) => (
                <li key={f} className={`flex items-start gap-2 text-sm ${i === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                  {i === 0 ? <span className="w-5" /> : <Check />}{f}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              {isPremium ? (
                <button
                  onClick={() => navigate('/billing')}
                  className="block w-full text-center px-5 py-3 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200"
                >
                  Manage Subscription
                </button>
              ) : (
                <button
                  onClick={startCheckout}
                  disabled={loading}
                  className="block w-full text-center px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Redirecting…' : 'Upgrade to Premium'}
                </button>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8 max-w-xl mx-auto">
          Secure payment by Stripe. Cancel anytime from the billing portal. Informational
          only — not investment advice.
        </p>
      </div>
    </div>
  );
};

export default Pricing;
